import { getType } from 'typesafe-actions';

// Utils
import { findIndexById, update, findIndex, remove } from '../util/general';
// Constants
// Actions
import { CheckoutActions, checkoutActions } from '../actions';
// Models
import {
  BookingModel,
  BasketModel,
  TripModel,
  HotelModel,
  TravelBookingModel,
  BasketItemModel,
  BookingItemModel,
  RentalModel,
  RedeemingAllowanceModel
} from '../models';
// Interfaces

export interface ICheckoutState {
  currentBooking:BookingModel;
  currentBookingItems:BookingItemModel[];
  basket:BasketModel;
  trips:TripModel[];
  hotels:HotelModel[];
  rentals:RentalModel[];
  travelBooking:TravelBookingModel;
  basketItems:BasketItemModel[];
  lastWSUpdated:number;
  seatMapSelectorUrl:string;
  redeemingAllowance:RedeemingAllowanceModel;
}

export const initialState: ICheckoutState = {
  currentBooking: null,
  currentBookingItems: [],
  basket: null,
  trips: [],
  hotels: [],
  rentals: [],
  basketItems: [],
  travelBooking: null,
  lastWSUpdated: -1,
  seatMapSelectorUrl: null,
  redeemingAllowance: null
};

// What the basket item selects: a tariff, a hotel fare or a rental fare. Exactly one of the three is
// set, so comparing all three tells a hotel item apart from a rental one.
const sameSelectionAs = (item:BasketItemModel) => (other:BasketItemModel) =>
  other.assemblyTariffId === item.assemblyTariffId
    && other.hotelFareId === item.hotelFareId
    && other.rentalFareId === item.rentalFareId;

export default (state:ICheckoutState = initialState, action:CheckoutActions) => {
  switch (action.type) {
    case getType(checkoutActions.setBasket):
      return { ...state, basket: action.payload };
    case getType(checkoutActions.setTrips):
      return { ...state, trips: action.payload };
    case getType(checkoutActions.setHotels):
      return { ...state, hotels: action.payload };
    case getType(checkoutActions.setRentals):
      return { ...state, rentals: action.payload };
    case getType(checkoutActions.setLastWSUpdated):
      // console.log(`lastWSUpdated Checkout: ${action.payload}`)
      return { ...state, lastWSUpdated: action.payload };
    case getType(checkoutActions.setCurrentBooking):
      return { ...state, currentBooking: action.payload };
    case getType(checkoutActions.setTravelBooking):
      return { ...state, travelBooking: action.payload };
    case getType(checkoutActions.setSeatMapSelectorUrl):
      return { ...state, seatMapSelectorUrl: action.payload };
    case getType(checkoutActions.setRedeemingAllowance):
      return { ...state, redeemingAllowance: action.payload };
    case getType(checkoutActions.addBaksetItem):
      // A destroyed item is the backend telling us this selection is gone. We only ever merged, so
      // it used to stay in the list and check-out kept rendering the previously selected fare
      // alongside the new one. Keyed on the item id, the only thing that identifies the row the
      // backend destroyed.
      if (action.payload.destroyed) {
        const destroyedIdx = findIndexById(action.payload.id)(state.basketItems);
        return {
          ...state,
          basketItems: destroyedIdx === -1
            ? state.basketItems : remove(destroyedIdx, 1, state.basketItems),
        };
      }
      // If we add the same selection to the basket, the backend removes the old item and replaces it
      // with a new one carrying a different id, so the id cannot find it. All three fare ids
      // together, because assemblyTariffId alone is null on every hotel and rental item: they would
      // share one key and a rental selection would overwrite the hotel one.
      const basketItemIdx = findIndex(sameSelectionAs(action.payload))(state.basketItems);
      return {
        ...state,
        basketItems: basketItemIdx === -1
          ? [...state.basketItems, action.payload] : update(basketItemIdx, action.payload, state.basketItems),
      };
    case getType(checkoutActions.addBookingItem):
      const bookingItemIdx = findIndexById(action.payload.id)(state.currentBookingItems);
      return {
        ...state,
        currentBookingItems: bookingItemIdx === -1 ?
          [...state.currentBookingItems, action.payload]
          :
          update(bookingItemIdx, action.payload, state.currentBookingItems),
      };
    case getType(checkoutActions.clearBasket):
      return initialState;
    default:
      return state;
  }
};
